import React, { useMemo } from "react";

import momentjs from "moment";

import { MOMENTJS_OPTIONS } from "Constants/constants";
import { useGetUserAvatar } from "Hooks/useGetAvatar";
import activityTemplates from "src/organization/common/components/activities/activityTemplates";

import ActivityDate from "../ActivityDate";
import StatusIcon from "../StatusIcon/StatusIcon";

import * as S from "./ActivityHeader.styles";

import type { Activity } from "@packages/client";

const ActivityHeader = ({
  activity,
  organization
}: {
  activity: Activity;
  organization: any;
}) => {
  momentjs.updateLocale("en", MOMENTJS_OPTIONS);
  const avatarPicture = useGetUserAvatar({
    user: activity?.payload?.user,
    organization
  });

  const [icon, title] = useMemo(() => {
    const template =
      activityTemplates[activity.type] || activityTemplates.unknown;
    const title = template.title ? template.title({ activity }) : "";
    return [template.icon, title];
  }, [activity]);

  const lastCommit = useMemo(() => {
    return activity.payload?.commits?.slice(-1).pop();
  }, [activity.payload?.commits]);

  const duration = useMemo(() => {
    const endTime = activity.completed_at ?? activity.cancelled_at;
    const startTime = activity.started_at ?? activity.created_at;

    const isEndTimeValid = momentjs(endTime).isValid();
    const isStartTimeValid = momentjs(startTime).isValid();

    // In the occasion that the "duration" returns "Invalid date", show nothing
    if (!isEndTimeValid || !isStartTimeValid) return "";

    return momentjs
      .duration(momentjs(endTime).diff(momentjs(startTime)))
      .humanize({ s: 60, m: 60, h: 24, d: 28, M: 11 });
  }, [
    activity.completed_at,
    activity.cancelled_at,
    activity.started_at,
    activity.created_at
  ]);

  return (
    <S.Wrapper>
      <S.Content>
        <S.Left>
          <S.Icon>{icon}</S.Icon>
          <S.Avatar
            userName={activity?.payload?.user?.display_name}
            userImage={avatarPicture}
            size="small"
            withToolTip
            alt="avatar"
          />
          <S.Event>
            <S.Title>{title}</S.Title>
            {lastCommit && (
              <S.DetailsWrapper>
                <S.Sha>{lastCommit.sha?.substring(0, 7)}</S.Sha>
                <S.Message>{lastCommit?.message}</S.Message>
              </S.DetailsWrapper>
            )}
          </S.Event>
          <S.DateTime>
            <S.Duration>{duration}</S.Duration>
            <S.Date>
              <ActivityDate date={activity.created_at} />
            </S.Date>
          </S.DateTime>
        </S.Left>
        <StatusIcon state={activity.state} result={activity.result} />
      </S.Content>
    </S.Wrapper>
  );
};

export default ActivityHeader;
